import { Component } from 'react'
import Icon from '../lib/icon.jsx'
import { captureException } from '../lib/sentry.js'

const LAST_ERROR_KEY = 'mp-last-error'

// Persist the most recent crash to localStorage so we can read it back
// on the next launch even if the user tapped Restart. Surviving the
// reload is the key — without this, "Restart App" wipes the React tree
// and any in-memory error context with it, leaving us blind on iOS.
function persistError(error, errorInfo, name) {
  try {
    const payload = {
      at: new Date().toISOString(),
      name: error?.name || 'Error',
      message: String(error?.message || error || 'Unknown error'),
      stack: String(error?.stack || '').split('\n').slice(0, 6).join('\n'),
      componentStack: String(errorInfo?.componentStack || '').split('\n').slice(0, 6).join('\n'),
      boundary: name || 'root',
      ua: navigator.userAgent,
    }
    localStorage.setItem(LAST_ERROR_KEY, JSON.stringify(payload))
  } catch {}
}

export function readLastCrash() {
  try {
    const raw = localStorage.getItem(LAST_ERROR_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function clearLastCrash() {
  try { localStorage.removeItem(LAST_ERROR_KEY) } catch {}
}

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    persistError(error, errorInfo, this.props.name)
    try {
      captureException(error, { errorInfo, boundary: this.props.name || 'root' })
    } catch {}
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.name || 'root', error, errorInfo)
    this.setState({ errorInfo })
  }

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state
      const message = String(error?.message || error || 'Unknown error')
      const stack = String(error?.stack || '').split('\n').slice(0, 6).join('\n')
      const compStack = String(errorInfo?.componentStack || '').split('\n').slice(0, 6).join('\n')

      return (
        <div className="fixed inset-0 bg-[var(--inv-bg)] flex flex-col items-center justify-center px-6 text-center overflow-y-auto py-12">
          <div className="w-16 h-16 rounded-2xl bg-[var(--inv-bg-alt)] flex items-center justify-center mb-5">
            <Icon name="alert" size={28} className="text-[var(--inv-muted)]" />
          </div>
          <h2 className="text-[22px] font-semibold text-[var(--inv-heading)] mb-2">Something went wrong</h2>
          <p
            className="text-[14px] text-[var(--inv-muted)] mb-4 break-words max-w-[320px]"
            style={{ overflowWrap: 'anywhere' }}
          >
            {message}
          </p>

          <button
            type="button"
            onClick={() => this.setState({ showDetails: !showDetails })}
            className="text-[12px] font-medium text-[var(--inv-muted)] underline mb-4"
          >
            {showDetails ? 'Hide details' : 'Show details'}
          </button>

          {showDetails && (
            <pre
              className="text-[10px] text-left whitespace-pre-wrap bg-[var(--inv-bg-alt)] rounded-lg p-3 mb-4 max-w-full overflow-x-auto"
              style={{
                color: 'var(--inv-muted)',
                fontFamily: 'ui-monospace, Menlo, monospace',
                maxHeight: 240,
                width: 'min(360px, 100%)',
              }}
            >
{`boundary: ${this.props.name || 'root'}
${stack || '(no stack)'}

component:
${compStack || '(no component stack)'}`}
            </pre>
          )}

          <button
            type="button"
            onClick={() => {
              this.setState({ hasError: false, error: null, errorInfo: null })
              window.location.href = '/'
            }}
            className="px-6 h-[48px] rounded-2xl bg-[var(--inv-heading)] text-[var(--inv-bg)] font-medium text-[16px] cursor-pointer active:scale-[0.96]"
          >
            Restart App
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
